import { toast } from "sonner";
import { CalendarCheck, FileSignature, ChevronDown, Newspaper, Hourglass } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { api, formatApiError } from "../lib/api";

export const STAGES = { meeting: { label: "Confirmed Meeting", icon: CalendarCheck, tag: "bg-sky-600 text-white" }, contract: { label: "Contract Sent", icon: FileSignature, tag: "bg-amber-500 text-amber-950" }, news: { label: "More News Coming", icon: Newspaper, tag: "bg-rose-500 text-white" } };

// Jeff only: show just the Potential deals with one status.
export const DealStageFilter = ({ value, onChange }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="w-56 rounded-full bg-white" data-testid="deal-stage-filter"><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all" data-testid="deal-stage-filter-all">Any deal status</SelectItem>
      {Object.entries(STAGES).map(([k, s]) => <SelectItem key={k} value={k} data-testid={`deal-stage-filter-${k}`}>{s.label}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const DealStageTag = ({ lead, size = "text-[11px]" }) => {
  const s = STAGES[lead.deal_stage];
  if (!s || lead.bucket !== "potential") return null;
  return (
    <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold ${s.tag} ${size}`} data-testid={`lead-stage-tag-${lead.id}`}>
      <s.icon className="h-3 w-3" />{s.label}
    </span>
  );
};

// Days in Potential: grey under a week, amber 7-13 days, red "stale" from 14 days.
export const DealAgeTag = ({ lead, size = "text-[11px]" }) => {
  const since = lead.potential_at || lead.bucket_at;
  if (lead.bucket !== "potential" || !since) return null;
  const days = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 86400000));
  const tone = days >= 14 ? "bg-red-100 text-red-800" : days >= 7 ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700";
  const label = days === 0 ? "In Potential today" : `${days} day${days === 1 ? "" : "s"} in Potential${days >= 14 ? " · follow up" : ""}`;
  return (
    <span className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full font-semibold ${tone} ${size}`} title={`In Potential since ${new Date(since).toLocaleDateString()}`} data-testid={`lead-age-tag-${lead.id}`}>
      <Hourglass className="h-3 w-3" />{label}
    </span>
  );
};

// Jeff only: one "Deal status" button with Confirmed Meeting, Contract Sent and More News Coming.
export const DealStageButtons = ({ lead, onChanged, compact }) => {
  const set = async (stage) => {
    try {
      const { data } = await api.post(`/leads/${lead.id}/stage`, { stage });
      if (!stage) toast.success("Deal status cleared");
      else if (data.rep_emailed) toast.success(`${STAGES[stage].label} · ${data.rep} has been emailed`);
      else toast.warning(`${STAGES[stage].label} set, but ${data.rep || "the rep"} has no email on file`);
      onChanged(data.lead);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cur = STAGES[lead.deal_stage];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`deal-status-${lead.id}`} onClick={(e) => e.stopPropagation()}
          className={`${compact ? "h-8 px-2.5 text-xs" : "h-11 w-full text-sm"} rounded-full border font-semibold flex items-center justify-center gap-1 whitespace-nowrap transition-colors ${cur ? `${cur.tag} border-transparent` : "bg-white border-slate-300 text-slate-800 hover:border-blue-400"}`}>
          {cur ? <cur.icon className="h-3.5 w-3.5" /> : null}{cur ? cur.label : "Deal status"}<ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {Object.entries(STAGES).map(([key, s]) => (
          <DropdownMenuItem key={key} disabled={lead.deal_stage === key} onSelect={() => set(key)} data-testid={`deal-status-${key}-${lead.id}`}>
            <s.icon className="h-4 w-4 mr-2" />{s.label}
          </DropdownMenuItem>
        ))}
        {cur && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => set(null)} data-testid={`deal-status-clear-${lead.id}`}>Clear status</DropdownMenuItem></>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
